import React, { useState, useRef, useEffect } from 'react';
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";
import { 
  X, 
  User, 
  Calendar, 
  Clock, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Send,
  Loader,
  Briefcase
} from 'lucide-react';

const LeaveRequestModal = ({ 
  isOpen, 
  onClose, 
  notification, 
  theme = 'light',
  colors 
}) => {
  const { profile } = useProfile();
  const [decision, setDecision] = useState('');
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leaveDetails, setLeaveDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const modalRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // Define default colors if not provided
  const defaultColors = {
    light: {
      modalBg: 'rgba(255, 255, 255, 0.98)',
      sidebarBorder: 'rgba(0, 0, 0, 0.08)',
      text: '#0f172a',
      textSecondary: '#64748b',
      inputBg: 'rgba(248, 250, 252, 0.8)',
      notificationShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
    },
    dark: {
      modalBg: 'rgba(15, 23, 42, 0.98)',
      sidebarBorder: 'rgba(255, 255, 255, 0.08)',
      text: '#ffffff',
      textSecondary: 'rgba(255, 255, 255, 0.7)',
      inputBg: 'rgba(255, 255, 255, 0.08)',
      notificationShadow: '0 20px 60px rgba(0, 0, 0, 0.6)'
    }
  };

  const c = colors ? colors[theme] : defaultColors[theme];

  useEffect(() => {
    if (isOpen && notification) {
      fetchLeaveDetails();
    }
  }, [isOpen, notification]);

  const fetchLeaveDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const leaveRequestId = notification.leaveId;
      const response = await apiFetch(`${BASE_URL}/leaves/${leaveRequestId}`, { method: "GET" });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setLeaveDetails(result.data);
      } else {
        setLeaveDetails({
          employeeName: notification.from || 'Unknown Employee',
          leaveType: 'Annual Leave',
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          duration: '7 days',
          reason: notification.message || 'Leave request submitted',
          requestDate: notification.createdAt || new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error fetching leave details:', error);
      setError('Failed to load leave request details');
      
      setLeaveDetails({
        employeeName: notification.from || 'Unknown Employee',
        leaveType: 'Annual Leave',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        duration: '7 days',
        reason: notification.message || 'Leave request submitted',
        requestDate: notification.createdAt || new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDecision = async () => {
    if (!decision) {
      alert('Please select approve or disapprove');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        notificationId: notification.id,
        leaveId: notification.leaveId || notification.id,
        decision: decision,
        comments: comments.trim(),
        approvedBy: profile?.employeeName || localStorage.getItem("employeeName") || "Manager",
        reviewDate: new Date().toISOString()
      };

      const response = await apiFetch(`${BASE_URL}/leaves/${payload.leaveId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(`Leave request ${decision} successfully!`);
        setDecision('');
        setComments('');
        onClose();
        window.location.reload();
      } else {
        throw new Error(result.message || 'Failed to submit decision');
      }

    } catch (error) {
      console.error('Error submitting decision:', error);
      alert('Failed to submit decision. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !notification) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      zIndex: 1003,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backdropFilter: 'blur(12px)',
      animation: 'fadeIn 0.3s ease',
      padding: '20px'
    },

    modal: {
      background: c.modalBg,
      backdropFilter: 'blur(24px)',
      border: `1px solid ${c.sidebarBorder}`,
      borderRadius: '24px',
      width: '100%',
      maxWidth: '680px',
      maxHeight: '90vh',
      overflow: 'hidden',
      boxShadow: '0 25px 80px rgba(0, 0, 0, 0.3), 0 0 1px rgba(255, 255, 255, 0.1) inset',
      animation: 'slideInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
      display: 'flex',
      flexDirection: 'column'
    },

    header: {
      padding: '28px 32px',
      borderBottom: `1px solid ${c.sidebarBorder}`,
      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(99, 102, 241, 0.05))',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'relative',
      overflow: 'hidden'
    },

    headerGlow: {
      position: 'absolute',
      top: '-50%',
      left: '-50%',
      width: '200%',
      height: '200%',
      background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
      pointerEvents: 'none'
    },

    title: {
      fontSize: '22px',
      fontWeight: '700',
      color: c.text,
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      position: 'relative',
      zIndex: 1
    },

    titleIcon: {
      background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
      borderRadius: '12px',
      padding: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)'
    },

    closeBtn: {
      background: c.inputBg,
      border: `1px solid ${c.sidebarBorder}`,
      color: c.textSecondary,
      cursor: 'pointer',
      padding: '10px',
      borderRadius: '10px',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      zIndex: 1
    },

    scrollContent: {
      overflowY: 'auto',
      flex: 1
    },

    content: {
      padding: '32px'
    },

    section: {
      marginBottom: '28px'
    },

    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: `2px solid ${c.sidebarBorder}`
    },

    sectionTitle: {
      fontSize: '17px',
      fontWeight: '700',
      color: c.text,
      flex: 1
    },

    sectionBadge: {
      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(99, 102, 241, 0.1))',
      color: '#3b82f6',
      padding: '6px 14px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      border: `1px solid rgba(59, 130, 246, 0.2)`
    },

    employeeCard: {
      background: `linear-gradient(135deg, ${c.inputBg}, ${c.inputBg})`,
      border: `1px solid ${c.sidebarBorder}`,
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
      position: 'relative',
      overflow: 'hidden'
    },

    employeeAvatar: {
      width: '72px',
      height: '72px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '28px',
      fontWeight: '700',
      flexShrink: 0,
      boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
      border: '3px solid white'
    },

    employeeInfo: {
      flex: 1
    },

    employeeName: {
      fontSize: '20px',
      fontWeight: '700',
      color: c.text,
      marginBottom: '6px'
    },

    employeeMeta: {
      fontSize: '14px',
      color: c.textSecondary,
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },

    detailsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px'
    },

    detailCard: {
      background: c.inputBg,
      border: `1px solid ${c.sidebarBorder}`,
      borderRadius: '14px',
      padding: '18px',
      transition: 'all 0.3s ease'
    },

    detailHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '12px'
    },

    detailIconWrapper: {
      width: '38px',
      height: '38px',
      borderRadius: '10px',
      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(99, 102, 241, 0.08))',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#3b82f6'
    },

    detailLabel: {
      fontSize: '12px',
      color: c.textSecondary,
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.8px'
    },

    detailValue: {
      fontSize: '15px',
      color: c.text,
      fontWeight: '600',
      lineHeight: '1.5'
    },

    reasonCard: {
      background: `linear-gradient(135deg, ${c.inputBg}, ${c.inputBg})`,
      border: `2px solid ${c.sidebarBorder}`,
      borderRadius: '16px',
      padding: '24px',
      marginTop: '20px'
    },

    reasonLabel: {
      fontSize: '13px',
      color: c.textSecondary,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },

    reasonText: {
      fontSize: '15px',
      color: c.text,
      lineHeight: '1.7',
      margin: 0
    },

    decisionSection: {
      background: `linear-gradient(135deg, ${c.inputBg}, ${c.inputBg})`,
      border: `1px solid ${c.sidebarBorder}`,
      borderRadius: '16px',
      padding: '24px'
    },

    decisionButtons: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
      marginBottom: '20px'
    },

    decisionBtn: {
      padding: '18px 24px',
      border: '2px solid',
      borderRadius: '14px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
      background: 'transparent'
    },

    approveBtn: {
      borderColor: decision === 'approved' ? '#22c55e' : c.sidebarBorder,
      color: decision === 'approved' ? 'white' : c.textSecondary,
      background: decision === 'approved' ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'transparent',
      boxShadow: decision === 'approved' ? '0 8px 24px rgba(34, 197, 94, 0.3)' : 'none',
      transform: decision === 'approved' ? 'scale(1.02)' : 'scale(1)'
    },

    disapproveBtn: {
      borderColor: decision === 'disapproved' ? '#ef4444' : c.sidebarBorder,
      color: decision === 'disapproved' ? 'white' : c.textSecondary,
      background: decision === 'disapproved' ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'transparent',
      boxShadow: decision === 'disapproved' ? '0 8px 24px rgba(239, 68, 68, 0.3)' : 'none',
      transform: decision === 'disapproved' ? 'scale(1.02)' : 'scale(1)'
    },

    textarea: {
      width: '100%',
      minHeight: '120px',
      background: c.inputBg,
      border: `2px solid ${c.sidebarBorder}`,
      borderRadius: '14px',
      padding: '18px',
      fontSize: '15px',
      color: c.text,
      outline: 'none',
      resize: 'vertical',
      transition: 'all 0.3s ease',
      fontFamily: 'inherit',
      lineHeight: '1.6'
    },

    footer: {
      padding: '24px 32px',
      borderTop: `1px solid ${c.sidebarBorder}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '14px',
      background: `linear-gradient(to top, ${c.inputBg}, transparent)`
    },

    cancelBtn: {
      background: 'transparent',
      border: `2px solid ${c.sidebarBorder}`,
      color: c.textSecondary,
      padding: '14px 28px',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '15px',
      fontWeight: '600',
      transition: 'all 0.3s ease'
    },

    submitBtn: {
      background: decision === 'approved' 
        ? 'linear-gradient(135deg, #22c55e, #16a34a)' 
        : decision === 'disapproved' 
        ? 'linear-gradient(135deg, #ef4444, #dc2626)'
        : 'linear-gradient(135deg, #3b82f6, #6366f1)',
      color: 'white',
      border: 'none',
      padding: '14px 32px',
      borderRadius: '12px',
      cursor: isSubmitting || !decision ? 'not-allowed' : 'pointer',
      fontSize: '15px',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      opacity: isSubmitting || !decision ? 0.5 : 1,
      boxShadow: (!isSubmitting && decision) ? '0 8px 24px rgba(59, 130, 246, 0.4)' : 'none'
    },

    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 40px',
      color: c.textSecondary
    },

    errorContainer: {
      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(220, 38, 38, 0.08))',
      border: '2px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '16px',
      padding: '24px',
      color: '#ef4444',
      fontSize: '15px',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      margin: '20px 0'
    }
  };

  return (
    <div style={styles.overlay}>
      <div ref={modalRef} style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.headerGlow} />
          <div style={styles.title}>
            <div style={styles.titleIcon}>
              <FileText size={24} color="white" />
            </div>
            <span>Leave Request Review</span>
          </div>
          <button style={styles.closeBtn} onClick={onClose} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        <div style={styles.scrollContent} className="modal-scroll">
          <div style={styles.content}>
            {loading ? (
              <div style={styles.loadingContainer}>
                <Loader size={40} className="spin" style={{marginBottom: '16px'}} />
                <span style={{fontSize: '16px', fontWeight: '600'}}>Loading leave request details...</span>
              </div>
            ) : error ? (
              <div style={styles.errorContainer}>
                <AlertCircle size={24} />
                <div>
                  <div style={{fontWeight: '700', marginBottom: '4px'}}>Error Loading Request</div>
                  <div style={{fontSize: '14px', opacity: 0.9}}>{error}</div>
                </div>
              </div>
            ) : leaveDetails ? (
              <>
                <div style={styles.employeeCard}>
                  <div style={styles.employeeAvatar}>
                    {leaveDetails.employeeName?.charAt(0).toUpperCase()}
                  </div>
                  <div style={styles.employeeInfo}>
                    <div style={styles.employeeName}>{leaveDetails.employeeName}</div>
                    <div style={styles.employeeMeta}>
                      <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                        <Briefcase size={14} />
                        Employee
                      </span>
                      <span>•</span>
                      <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                        <Calendar size={14} />
                        Requested {new Date(leaveDetails.requestDate).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={styles.section}>
                  <div style={styles.sectionHeader}>
                    <div style={styles.sectionTitle}>Request Details</div>
                    <div style={styles.sectionBadge}>{leaveDetails.leaveType}</div>
                  </div>
                  
                  <div style={styles.detailsGrid}>
                    <div style={styles.detailCard}>
                      <div style={styles.detailHeader}>
                        <div style={styles.detailIconWrapper}>
                          <Calendar size={18} />
                        </div>
                        <div style={styles.detailLabel}>Start Date</div>
                      </div>
                      <div style={styles.detailValue}>
                        {new Date(leaveDetails.startDate).toLocaleDateString('en-US', { 
                          month: 'long', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </div>
                    </div>

                    <div style={styles.detailCard}>
                      <div style={styles.detailHeader}>
                        <div style={styles.detailIconWrapper}>
                          <Calendar size={18} />
                        </div>
                        <div style={styles.detailLabel}>End Date</div>
                      </div>
                      <div style={styles.detailValue}>
                        {new Date(leaveDetails.endDate).toLocaleDateString('en-US', { 
                          month: 'long', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </div>
                    </div>

                    <div style={styles.detailCard}>
                      <div style={styles.detailHeader}>
                        <div style={styles.detailIconWrapper}>
                          <Clock size={18} />
                        </div>
                        <div style={styles.detailLabel}>Duration</div>
                      </div>
                      <div style={styles.detailValue}>{leaveDetails.duration}</div>
                    </div>
                  </div>
                  
                  {leaveDetails.reason && (
                    <div style={styles.reasonCard}>
                      <div style={styles.reasonLabel}>
                        <FileText size={16} />
                        Reason for Leave
                      </div>
                      <p style={styles.reasonText}>{leaveDetails.reason}</p>
                    </div>
                  )}
                </div>

                <div style={styles.section}>
                  <div style={styles.decisionSection}>
                    <div style={styles.sectionHeader}>
                      <div style={styles.sectionTitle}>Your Decision</div>
                    </div>
                    
                    <div style={styles.decisionButtons}>
                      <button
                        style={{...styles.decisionBtn, ...styles.approveBtn}}
                        onClick={() => setDecision('approved')}
                        disabled={isSubmitting}
                      >
                        <CheckCircle size={22} />
                        Approve Leave
                      </button>
                      <button
                        style={{...styles.decisionBtn, ...styles.disapproveBtn}}
                        onClick={() => setDecision('disapproved')}
                        disabled={isSubmitting}
                      >
                        <XCircle size={22} />
                        Disapprove Leave
                      </button>
                    </div>

                    {decision && (
                      <div>
                        <div style={styles.reasonLabel}>
                          <FileText size={16} />
                          {decision === 'approved' ? 'Approval Comments' : 'Reason for Disapproval'} (Optional)
                        </div>
                        <textarea
                          style={styles.textarea}
                          placeholder={
                            decision === 'approved' 
                              ? "Add any notes about this approval..."
                              : "Please explain why this request is being disapproved..."
                          }
                          value={comments}
                          onChange={(e) => setComments(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            style={styles.submitBtn}
            onClick={handleSubmitDecision}
            disabled={!decision || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader size={18} className="spin" />
                Processing...
              </>
            ) : (
              <>
                <Send size={18} />
                Submit {decision === 'approved' ? 'Approval' : decision === 'disapproved' ? 'Disapproval' : 'Decision'}
              </>
            )}
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes slideInScale {
            from { 
              opacity: 0; 
              transform: translateY(-30px) scale(0.9);
            }
            to { 
              opacity: 1; 
              transform: translateY(0) scale(1);
            }
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .spin {
            animation: spin 1s linear infinite;
          }

          .modal-scroll::-webkit-scrollbar {
            width: 8px;
          }

          .modal-scroll::-webkit-scrollbar-track {
            background: transparent;
          }

          .modal-scroll::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(99, 102, 241, 0.2));
            border-radius: 10px;
          }

          .modal-scroll::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.5), rgba(99, 102, 241, 0.4));
          }
        `}
      </style>
    </div>
  );
};

export default LeaveRequestModal;